/**
 * ProtectedRoute Component
 *
 * Route-level protection for React Router:
 * - Checks authentication status
 * - Validates permissions and roles
 * - Redirects unauthorized users
 * - Shows appropriate error states
 *
 * @security SEC-001: Role-Based Access Control Implementation
 * @priority P0 CRITICAL
 *
 * Peer Review Completed:
 * - Gemini 3 Pro: 7/10
 * - GPT-4o: 8/10
 *
 * SECURITY NOTICE:
 * This component provides UI-level route protection only.
 * Users can bypass this by manipulating React DevTools or state.
 * ALL API endpoints called by protected routes MUST validate
 * permissions server-side and return 403 if unauthorized.
 *
 * This is UX protection (don't show what they can't use),
 * NOT security enforcement (that's the backend's job).
 */

import React, { useEffect, useMemo, memo } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { useAuthStore } from '../../stores/authStore';
import type { ProtectedRouteProps, Permission, UserRole, Role } from '../../types/auth';
import { ROLE_DISPLAY_NAMES } from '../../types/auth';
import PermissionDenied from './PermissionDenied';

// ============================================================================
// Component
// ============================================================================

/**
 * ProtectedRoute - Wraps routes that require authentication and/or specific permissions
 *
 * @example
 * ```tsx
 * // Require authentication only
 * <ProtectedRoute>
 *   <Dashboard />
 * </ProtectedRoute>
 *
 * // Require specific permission
 * <ProtectedRoute requiredPermissions={['admin:view']}>
 *   <AdminPanel />
 * </ProtectedRoute>
 *
 * // Require minimum role
 * <ProtectedRoute requiredRole="senior_analyst">
 *   <SensitiveOperations />
 * </ProtectedRoute>
 *
 * // Require any of multiple permissions
 * <ProtectedRoute
 *   requiredPermissions={['policies:edit', 'admin:edit']}
 *   requireAll={false}
 * >
 *   <PolicyEditor />
 * </ProtectedRoute>
 * ```
 */
const ProtectedRoute: React.FC<ProtectedRouteProps> = memo(({
  requiredPermissions,
  requiredRole,
  requireAll = true,
  redirectTo = '/login',
  children,
  fallback,
}) => {
  const location = useLocation();
  const {
    isAuthenticated,
    isLoading,
    user,
    hasAllPermissions,
    hasAnyPermission,
    hasMinimumRole,
    status,
    isSessionValid,
  } = useAuth();

  // Log permission check for audit
  const logAuditEvent = useAuthStore((state) => state.logAuditEvent);

  // Calculate authorization status
  const authorizationStatus = useMemo(() => {
    // Not authenticated
    if (!isAuthenticated || !user) {
      return { authorized: false, reason: 'unauthenticated' as const };
    }

    // Check role requirement
    if (requiredRole) {
      // Types are now unified - no casting needed
      if (!hasMinimumRole(requiredRole)) {
        return {
          authorized: false,
          reason: 'insufficient_role' as const,
          requiredRole,
          userRole: user.role,
        };
      }
    }

    // Check permissions
    // Permission types are compatible - pass directly
    if (requiredPermissions && requiredPermissions.length > 0) {
      const hasPerms = requireAll
        ? hasAllPermissions(requiredPermissions)
        : hasAnyPermission(requiredPermissions);

      if (!hasPerms) {
        return {
          authorized: false,
          reason: 'missing_permissions' as const,
          requiredPermissions,
        };
      }
    }

    return { authorized: true, reason: 'authorized' as const };
  }, [
    isAuthenticated,
    user,
    requiredRole,
    requiredPermissions,
    requireAll,
    hasMinimumRole,
    hasAllPermissions,
    hasAnyPermission,
  ]);

  // Log access attempts
  useEffect(() => {
    if (user && requiredPermissions && requiredPermissions.length > 0) {
      const firstPermission = requiredPermissions[0];
      if (firstPermission) {
        const [category] = firstPermission.split(':');

        logAuditEvent(
          firstPermission,
          authorizationStatus.authorized,
          'route_access',
          category || 'unknown',
          location.pathname,
          {
            route: location.pathname,
            requiredPermissions,
            requiredRole,
          }
        );
      }
    }
  }, [
    authorizationStatus.authorized,
    user,
    requiredPermissions,
    requiredRole,
    location.pathname,
    logAuditEvent,
  ]);

  // ----------------------------------------------------------------
  // Loading State
  // ----------------------------------------------------------------

  if (isLoading || status === 'loading') {
    return (
      fallback || (
        <div className="flex items-center justify-center min-h-screen bg-slate-900">
          <div className="flex flex-col items-center gap-4">
            <div className="w-12 h-12 border-4 border-cyan-500/30 border-t-cyan-500 rounded-full animate-spin" />
            <span className="text-slate-400 text-sm">
              Verifying authorization...
            </span>
          </div>
        </div>
      )
    );
  }

  // ----------------------------------------------------------------
  // Not Authenticated - Redirect to Login
  // ----------------------------------------------------------------

  if (!isAuthenticated || status === 'unauthenticated') {
    return (
      <Navigate
        to={redirectTo}
        state={{ from: location, message: 'Please log in to continue' }}
        replace
      />
    );
  }

  // ----------------------------------------------------------------
  // Session Expired - Redirect to Login
  // ----------------------------------------------------------------

  if (status === 'session_expired') {
    return (
      <Navigate
        to={redirectTo}
        state={{ from: location, message: 'Your session has expired. Please log in again.' }}
        replace
      />
    );
  }

  // ----------------------------------------------------------------
  // MFA Required
  // ----------------------------------------------------------------

  if (status === 'mfa_required') {
    return (
      <Navigate
        to="/mfa"
        state={{ from: location }}
        replace
      />
    );
  }

  // ----------------------------------------------------------------
  // Not Authorized - Show Permission Denied
  // ----------------------------------------------------------------

  if (!authorizationStatus.authorized) {
    return (
      <PermissionDenied
        title="Access Denied"
        message={getAuthorizationMessage(authorizationStatus)}
        requiredPermissions={
          authorizationStatus.reason === 'missing_permissions'
            ? authorizationStatus.requiredPermissions
            : undefined
        }
        requiredRole={
          authorizationStatus.reason === 'insufficient_role'
            ? authorizationStatus.requiredRole
            : undefined
        }
        currentRole={user?.role}
        showBackButton
        showHomeButton
      />
    );
  }

  // ----------------------------------------------------------------
  // Authorized - Render Children
  // ----------------------------------------------------------------

  return <>{children}</>;
});

ProtectedRoute.displayName = 'ProtectedRoute';

// ============================================================================
// Helper Functions
// ============================================================================

interface AuthorizationResult {
  authorized: boolean;
  reason: 'authorized' | 'unauthenticated' | 'insufficient_role' | 'missing_permissions';
  requiredRole?: UserRole;
  userRole?: UserRole;
  requiredPermissions?: Permission[];
}

function getAuthorizationMessage(status: AuthorizationResult): string {
  switch (status.reason) {
    case 'unauthenticated':
      return 'You must be logged in to access this page.';
    case 'insufficient_role':
      return `This page requires ${getRoleDisplayName(status.requiredRole!)} access or higher. Your current role is ${getRoleDisplayName(status.userRole!)}.`;
    case 'missing_permissions':
      return 'You do not have the required permissions to access this page.';
    default:
      return 'Access denied.';
  }
}

function getRoleDisplayName(role: UserRole): string {
  return ROLE_DISPLAY_NAMES[role] || role;
}

// ============================================================================
// Exports
// ============================================================================

export default ProtectedRoute;

